import Image from 'next/image';
import Link from 'next/link';
import React, { useMemo } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { formatNumber } from '../../utils/format';
import { getPercentageString } from '../../utils/string';
import RenderLayout, { Platform } from '../core/render-layout';

type NameOccurrencesTableProps = {
  countries: {
    ID: number;
    living: number;
    count: number;
    name: string;
    iso_3: string;
    iso_2: string;
  }[];
};

export default function NameOccurrencesTable({
  countries,
}: Readonly<NameOccurrencesTableProps>) {
  const sortedCountries = useMemo(() => {
    if (!countries || !countries?.length || !Array.isArray(countries)) {
      return [];
    }

    const countriesCopy = [...countries];

    return countriesCopy?.sort((a, b) => {
      return b.count - a.count;
    });
  }, [countries]);

  const _renderMobileLayout = () => {
    if (!countries?.length) {
      return <></>;
    }

    return (
      <div
        className="bg-white
            rounded-lg border-[1px] border-[#E7E8E9] border-solid
            shadow-[0px_4px_14px_0px_rgba(39,48,62,0.03)]"
      >
        {sortedCountries?.map((country) => (
          <Link
            key={`mobile-${country.ID}`}
            href={`/country/${country.name?.toLowerCase()}`}
            className="flex flex-col py-[16px] px-[20px] border-b-[1px] border-y-[#E7E8E9] border-t-solid"
          >
            <div className="flex justify-between items-center">
              <div className="flex justify-start items-center pr-[8px]">
                <ReactCountryFlag
                  countryCode={country?.iso_2}
                  svg
                  alt={`${country?.name} flag`}
                  cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/1x1/"
                  cdnSuffix="svg"
                  style={{
                    borderRadius: '9999px',
                    width: '20px',
                    height: '20px',
                    marginRight: '8px',
                  }}
                />
                <span className="text-[14px] font-semibold text-[#0F1928]">
                  {country.name}
                </span>
              </div>
              <div className="flex justify-end items-center">
                <Image
                  alt="Go To Country Page"
                  src="/icons/chevron right gray.svg"
                  height={24}
                  width={24}
                />
              </div>
            </div>
            <div className="flex justify-between items-center mt-[16px]">
              <div className="flex justify-start items-center">
                <span className="text-[14px] font-normal text-[#0F1928]">
                  Occurrences
                </span>
              </div>
              <div className="flex justify-end items-center">
                <span className="text-[14px] font-normal tabular-nums text-[#0F1928]">
                  {formatNumber(country.count)}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center mt-[8px]">
              <div className="flex justify-start items-center">
                <span className="text-[14px] font-normal text-[#0F1928]">
                  Percentage
                </span>
              </div>
              <div className="flex justify-end items-center">
                <span className="text-[14px] font-normal tabular-nums text-[#0F1928]">
                  {getPercentageString(
                    country.living !== 0
                      ? (country.count / country.living) * 100
                      : 0,
                    {
                      fixedDecimals: 2,
                    }
                  )}
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    );
  };

  const _renderWebLayout = () => {
    if (!countries?.length) {
      return null;
    }

    return (
      <div
        className="bg-white rounded-lg
          border-[1px] border-[#E7E8E9] border-solid shadow-[0px_4px_14px_0px_rgba(39,48,62,0.03)]
          tablet:mt-[40px]"
      >
        <table className="w-full">
          <thead>
            <tr>
              <th className="py-[20px] px-[32px] w-[31%] font-semibold text-left text-[#0F1928]">
                Country
              </th>
              <th className="py-[20px] px-[32px] w-[23%] font-semibold text-right text-[#0F1928]">
                Occurrences
              </th>
              <th className="py-[20px] px-[32px] w-[23%] font-semibold text-right text-[#0F1928]">
                Percentage
              </th>
              <th className="py-[20px] px-[32px] w-[23%] font-semibold text-right text-[#0F1928]"></th>
            </tr>
          </thead>
          <tbody>
            {sortedCountries?.map((country) => (
              <tr
                key={country.ID}
                className="hover:bg-[#F7F7F8] border-t-[1px] border-y-[#E7E8E9] border-t-solid"
              >
                <td className="w-[31%]">
                  <Link
                    className="flex justify-start items-center py-[12px] px-[32px] w-full h-full font-normal text-[#0F1928]"
                    href={`/country/${country.name?.toLowerCase()}`}
                  >
                    <ReactCountryFlag
                      countryCode={country?.iso_2}
                      svg
                      alt={`${country?.name} flag`}
                      cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/1x1/"
                      cdnSuffix="svg"
                      style={{
                        borderRadius: '9999px',
                        width: '28px',
                        height: '28px',
                        border: '1px solid black',
                        marginRight: '10px',
                      }}
                    />
                    {country.name}
                  </Link>
                </td>
                <td className="w-[23%]">
                  <Link
                    className="flex justify-end items-center py-[12px] px-[32px] w-full h-full font-normal tabular-nums text-[#0F1928]"
                    href={`/country/${country.name?.toLowerCase()}`}
                  >
                    {formatNumber(country.count)}
                  </Link>
                </td>
                <td className="w-[23%] text-right">
                  <Link
                    className="flex justify-end items-center py-[12px] px-[32px] w-full h-full font-normal tabular-nums text-[#0F1928]"
                    href={`/country/${country.name?.toLowerCase()}`}
                  >
                    {getPercentageString(
                      country.living !== 0
                        ? (country.count / country.living) * 100
                        : 0,
                      {
                        fixedDecimals: 2,
                      }
                    )}
                  </Link>
                </td>
                <td className="relativew-[23%]">
                  <Link
                    className="flex justify-end items-center py-[12px] px-[32px] w-full h-full font-normal"
                    href={`/country/${country.name?.toLowerCase()}`}
                  >
                    <Image
                      alt="Go To Country Page"
                      src="/icons/chevron right gray.svg"
                      height={24}
                      width={24}
                    />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      <RenderLayout platforms={[Platform.DESKTOP]}>
        {_renderWebLayout()}
      </RenderLayout>
      <RenderLayout platforms={[Platform.MOBILE, Platform.TABLET]}>
        {_renderMobileLayout()}
      </RenderLayout>
    </div>
  );
}
